import React from 'react';
import theme from '../../theme/globalTheme';
import { Card, Alert, Steps, Typography, Divider, Tag } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, WarningOutlined } from '@ant-design/icons';

const { Title, Paragraph, Text } = Typography;

function TemplateGuide() {
  return (
    <div style={{ minHeight: '100vh', background: theme.colors.bgDark, padding: '20px' }} className="p-6">
      <Card>
        <Title level={2}>Guide de Création des Templates Word</Title>

        <Alert
          message="Problème fréquent : Variables cassées par le formatage"
          description="Word peut casser vos variables {{}} en insérant des balises XML cachées. Ce guide vous montre comment l'éviter."
          type="warning"
          showIcon
          icon={<WarningOutlined />}
          className="mb-6"
        />

        <Divider orientation="left">Étapes pour créer un template correct</Divider>

        <Steps
          direction="vertical"
          current={-1}
          items={[
            {
              title: 'Créer le document Word',
              description: (
                <div>
                  <Paragraph>
                    Ouvrez Microsoft Word et créez votre document comme d'habitude.
                  </Paragraph>
                </div>
              ),
            },
            {
              title: 'Taper TOUTES les variables EN TEXTE BRUT',
              description: (
                <div>
                  <Paragraph>
                    <Text strong>IMPORTANT :</Text> Ne formatez RIEN pour l'instant !
                  </Paragraph>
                  <Paragraph>
                    Tapez toutes vos variables d'un seul coup :
                  </Paragraph>
                  <pre style={{ background: '#f5f5f5', padding: '10px' }}>
{`{{today_date}}
{{client_name}}
{{charterer}}
{{charter_date}}
{{from_hour}}
{{to_hour}}
{{brand_of_vessel}}
{{length}}
{{cruising_area}}
{{pax_number}}
{{boat_price}}
{{total_price}}`}
                  </pre>
                </div>
              ),
            },
            {
              title: 'Appliquer le formatage APRÈS',
              description: (
                <div>
                  <Paragraph>
                    Une fois TOUTES les variables tapées, vous pouvez :
                  </Paragraph>
                  <ul>
                    <li>Mettre en gras</li>
                    <li>Changer la police</li>
                    <li>Changer la couleur</li>
                    <li>Aligner le texte</li>
                  </ul>
                  <Paragraph>
                    <Text type="danger">⚠️ Ne jamais éditer le contenu d'une variable après formatage !</Text>
                  </Paragraph>
                </div>
              ),
            },
            {
              title: 'Sauvegarder au bon endroit',
              description: (
                <div>
                  <Paragraph>
                    Uploadez votre fichier dans Supabase Storage :
                  </Paragraph>
                  <ul>
                    <li>Bucket : <Tag color="blue">templates</Tag></li>
                    <li>Noms possibles :
                      <Tag>Contrat.docx</Tag>
                      <Tag>Confirmation.docx</Tag>
                      <Tag>Facturation.docx</Tag>
                    </li>
                  </ul>
                </div>
              ),
            },
          ]}
        />

        <Divider orientation="left">Variables disponibles pour les Charters</Divider>

        <Card size="small" className="mb-4">
          <Title level={4}>Dates</Title>
          <ul>
            <li><Tag color="green">{'{{today_date}}'}</Tag> - Date du jour (génération)</li>
            <li><Tag color="green">{'{{generation_date}}'}</Tag> - Date de génération</li>
            <li><Tag color="green">{'{{charter_date}}'}</Tag> - Date du charter</li>
          </ul>
        </Card>

        <Card size="small" className="mb-4">
          <Title level={4}>Horaires</Title>
          <ul>
            <li><Tag color="blue">{'{{from_hour}}'}</Tag> - Heure de départ (ex: 09:00)</li>
            <li><Tag color="blue">{'{{to_hour}}'}</Tag> - Heure de retour (ex: 17:00)</li>
          </ul>
        </Card>

        <Card size="small" className="mb-4">
          <Title level={4}>Client</Title>
          <ul>
            <li><Tag color="purple">{'{{client_name}}'}</Tag> - Nom du client</li>
            <li><Tag color="purple">{'{{charterer}}'}</Tag> - Hôtel ou agence</li>
          </ul>
        </Card>

        <Card size="small" className="mb-4">
          <Title level={4}>Yacht</Title>
          <ul>
            <li><Tag color="orange">{'{{brand_of_vessel}}'}</Tag> - Marque (ex: Princess)</li>
            <li><Tag color="orange">{'{{length}}'}</Tag> - Longueur (ex: 55)</li>
          </ul>
        </Card>

        <Card size="small" className="mb-4">
          <Title level={4}>Détails</Title>
          <ul>
            <li><Tag color="cyan">{'{{cruising_area}}'}</Tag> - Zone de croisière</li>
            <li><Tag color="cyan">{'{{flight_info}}'}</Tag> - Informations de vol</li>
            <li><Tag color="cyan">{'{{pax_number}}'}</Tag> - Nombre de passagers</li>
          </ul>
        </Card>

        <Card size="small" className="mb-4">
          <Title level={4}>Prix</Title>
          <ul>
            <li><Tag color="red">{'{{boat_price}}'}</Tag> - Prix du bateau</li>
            <li><Tag color="red">{'{{vip_service}}'}</Tag> - Service VIP (formaté avec +)</li>
            <li><Tag color="red">{'{{sxm_taxes}}'}</Tag> - Taxes SXM (formaté avec +)</li>
            <li><Tag color="red">{'{{sbh_taxes}}'}</Tag> - Taxes SBH (formaté avec +)</li>
            <li><Tag color="red">{'{{axa_taxes}}'}</Tag> - Taxes AXA (formaté avec +)</li>
            <li><Tag color="red">{'{{total_price}}'}</Tag> - Prix total</li>
          </ul>
        </Card>

        <Divider orientation="left">Bon vs Mauvais exemple</Divider>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
          <Card
            size="small"
            title={<span><CloseCircleOutlined style={{ color: theme.colors.textTertiary }} /> MAUVAIS</span>}
            style={{ border: '2px solid #ff4d4f' }}
          >
            <Paragraph>
              <Text delete>{'Taper {{, puis mettre en gras, puis taper name}}'}</Text>
            </Paragraph>
            <Paragraph>
              <Text delete>Copier-coller des variables depuis un autre document</Text>
            </Paragraph>
            <Paragraph>
              <Text delete>Utiliser CTRL+Z après avoir tapé une variable</Text>
            </Paragraph>
          </Card>

          <Card
            size="small"
            title={<span><CheckCircleOutlined style={{ color: theme.colors.white }} /> BON</span>}
            style={{ border: '2px solid #52c41a' }}
          >
            <Paragraph>
              <Text strong>{"Taper {{client_name}} d'un seul coup, puis mettre en gras"}</Text>
            </Paragraph>
            <Paragraph>
              <Text strong>Taper toutes les variables en texte brut d'abord</Text>
            </Paragraph>
            <Paragraph>
              <Text strong>Formater seulement à la fin</Text>
            </Paragraph>
          </Card>
        </div>

        <Divider />

        <Alert
          message="Besoin d'aide ?"
          description="Si vous continuez à avoir des erreurs, supprimez TOUTES les variables du template et retapez-les en suivant ce guide."
          type="info"
          showIcon
        />
      </Card>
    </div>
  );
}

export default TemplateGuide;
